'use client'

import React, { useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Calendar, Loader2, Play } from 'lucide-react'
import { useNewsList } from '@/hook/news/use-news-list'
import { buildAssetUrl } from '@/utils/api-url'

export default function FeaturedActivities() {
  const { data, isLoading, error } = useNewsList({
    page: 1,
    pageSize: 6,
  })

  const activities = useMemo(() => {
    if (!data?.items) return []
    
    return data.items.slice(0, 6).map((item, index) => {
      const contentAssets = item.content_assets || []
      const videoAsset = contentAssets.find((asset: any) => asset.asset?.mime_type?.startsWith('video/'))
      const imageAsset = contentAssets.find((asset: any) => asset.asset?.mime_type?.startsWith('image/'))
      const thumbnail = item.thumbnail || (imageAsset ? imageAsset.asset.url : '')
      const thumbnailUrl = thumbnail ? buildAssetUrl(thumbnail) : '/assets/images/ex1.jpg'
      const videoUrl = videoAsset?.asset?.url ? buildAssetUrl(videoAsset.asset.url) : null
      
      const date = item.created_at ? new Date(item.created_at) : new Date()
      const days = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7']
      const dayName = days[date.getDay()]
      const dateText = `${dayName}, ${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`
      
      return {
        id: item.id?.toString() || item.public_id || `${index}`,
        title: item.title,
        slug: item.slug,
        dateText,
        thumbnail: thumbnailUrl,
        videoUrl,
      }
    })
  }, [data?.items])

  if (isLoading) {
    return (
      <section className='relative overflow-hidden bg-gradient-to-b from-white via-[#E8F5E9]/30 to-white py-24 md:py-32'>
        <div className='container mx-auto px-4'>
          <div className='flex justify-center items-center py-20'>
            <Loader2 className='size-10 animate-spin text-[#33B54A]' />
          </div>
        </div>
      </section>
    )
  }

  if (error || activities.length === 0) {
    return null
  }

  return (
    <motion.section
      id='featured'
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
      className='relative overflow-hidden bg-gradient-to-b from-white via-[#E8F5E9]/30 to-white py-24 md:py-32'
    >
      <div className='pointer-events-none absolute inset-0'>
        <motion.div
          className='absolute left-0 top-0 size-[500px] rounded-full bg-[#33B54A]/6 blur-3xl'
          animate={{ x: [0, 50, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className='absolute right-0 bottom-0 size-[500px] rounded-full bg-[#F78F1E]/6 blur-3xl'
          animate={{ x: [0, -50, 0], y: [0, 30, 0], scale: [1, 1.3, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className='container relative z-10 mx-auto px-4'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
          className='mx-auto mb-16 max-w-3xl text-center'
        >
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className='text-4xl font-black tracking-tight md:text-6xl'
          >
            <span className='text-[#33B54A]'>Hoạt động </span>
            <span className='text-[#F78F1E]'>nổi bật</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className='mt-5 text-lg leading-relaxed text-gray-600 md:text-xl'
          >
            Những khoảnh khắc hoạt động của bé, kỷ ức về tuổi thơ tại trường Trúc Xinh
          </motion.p>
        </motion.div>

        <div className='mx-auto grid max-w-7xl grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
          {activities.map((item, index) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: index * 0.08, ease: [0.4, 0, 0.2, 1] }}
              whileHover={{ y: -8, scale: 1.01 }}
              className='group relative overflow-hidden rounded-3xl bg-white shadow-md ring-1 ring-gray-100/50 transition-all duration-300 hover:shadow-xl hover:ring-[#33B54A]/30'
            >
              <Link href={`/activities/detail/${item.slug}`}>
                <div className='relative block h-[240px] w-full overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 md:h-[260px]'>
                  {item.videoUrl ? (
                    <>
                      <video
                        src={item.videoUrl}
                        className='size-full object-cover transition-transform duration-500 group-hover:scale-110'
                        muted
                        playsInline
                        loop
                      />
                      <div className='absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className='flex size-16 items-center justify-center rounded-full border-4 border-white/50 bg-white/95 shadow-2xl backdrop-blur-sm'
                        >
                          <Play className='ml-1 size-8 text-[#33B54A]' fill='currentColor' />
                        </motion.div>
                      </div>
                    </>
                  ) : (
                    <>
                      <Image
                        width={400}
                        height={260}
                        src={item.thumbnail}
                        alt={item.title}
                        className='size-full object-cover transition-transform duration-500 group-hover:scale-110'
                        unoptimized
                      />
                      <div className='absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent' />
                    </>
                  )}

                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    className='absolute left-4 top-4 z-10 flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold text-gray-800 backdrop-blur-md shadow-lg'
                  >
                    <Calendar className='size-3 text-[#33B54A]' />
                    <span>{item.dateText}</span>
                  </motion.div>
                </div>

                <div className='p-5 md:p-6'>
                  <h3 className='line-clamp-2 text-base font-black leading-snug text-gray-900 transition-colors group-hover:text-[#33B54A] md:text-lg'>
                    {item.title}
                  </h3>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </motion.section>
  )
}
